// src/lib/api/jobs/activity.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId, getValidatedUserId } from '../shared/helpers'
import { withErrorHandling, createSuccessResponse } from '../shared/errors'
import { validateRequired, sanitizeInput } from '../shared/validation'

/**
 * Get job activity
 * Migrated from supabaseApi.js lines 1795-1810 (approximate)
 */
const _getJobActivity = async (jobId, companyId = null) => {
  validateRequired(jobId, 'Job ID')
  
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('job_activity')
    .select('*')
    .eq('company_id', resolvedCompanyId)
    .eq('job_id', jobId)
    .order('created_at', { ascending: false })
  
  if (error) throw error
  return createSuccessResponse(data || [])
}

/**
 * Add job activity
 * Migrated from supabaseApi.js lines 1812-1830 (approximate)
 */
const _addJobActivity = async (activityData) => {
  validateRequired(activityData.job_id, 'Job ID')
  validateRequired(activityData.activity_type, 'Activity type')
  validateRequired(activityData.description, 'Description')
  
  const companyId = activityData.company_id || await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const userId = await getValidatedUserId()
  if (!userId) throw new Error('Not authenticated')

  const sanitizedData = {
    ...activityData,
    description: sanitizeInput(activityData.description),
    notes: activityData.notes ? sanitizeInput(activityData.notes) : null,
    company_id: companyId,
    created_by: userId
  }
  
  const { data, error } = await supabase
    .from('job_activity')
    .insert([sanitizedData])
    .select()
    .single()
  
  if (error) throw error
  return data
}

/**
 * Update job activity note
 * Migrated from supabaseApi.js lines 1832-1850 (approximate)
 */
const _updateJobActivityNote = async (activityId, note) => {
  validateRequired(activityId, 'Activity ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const sanitizedNote = note ? sanitizeInput(note) : null
  
  const { data, error } = await supabase
    .from('job_activity')
    .update({ 
      notes: sanitizedNote, 
      updated_at: new Date().toISOString() 
    })
    .eq('id', activityId)
    .eq('company_id', companyId)
    .select()
    .single()
  
  if (error) throw error
  return data
}

/**
 * Delete job activity
 */
const _deleteJobActivity = async (activityId) => {
  validateRequired(activityId, 'Activity ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { error } = await supabase
    .from('job_activity')
    .delete()
    .eq('id', activityId)
    .eq('company_id', companyId)
  
  if (error) throw error
  return { success: true }
}

/**
 * Get activity by type
 */
const _getJobActivityByType = async (jobId, activityType, companyId = null) => {
  validateRequired(jobId, 'Job ID')
  validateRequired(activityType, 'Activity type')
  
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('job_activity')
    .select('*')
    .eq('company_id', resolvedCompanyId)
    .eq('job_id', jobId)
    .eq('activity_type', activityType)
    .order('created_at', { ascending: false })
  
  if (error) throw error
  return createSuccessResponse(data || [])
}

// Export wrapped functions with error handling
export const getJobActivity = withErrorHandling(_getJobActivity, 'fetch job activity', 'job_activity')
export const addJobActivity = withErrorHandling(_addJobActivity, 'add job activity', 'job_activity')
export const updateJobActivityNote = withErrorHandling(_updateJobActivityNote, 'update job activity note', 'job_activity')
export const deleteJobActivity = withErrorHandling(_deleteJobActivity, 'delete job activity', 'job_activity')
export const getJobActivityByType = withErrorHandling(_getJobActivityByType, 'fetch job activity by type', 'job_activity')